import type { Listing } from '@thevillages/schemas';

import { Component, computed, effect, inject, signal, untracked } from '@angular/core';

import { FilteringService } from '../../services/filtering';
import { ListingsService } from '../../services/listings/listings';
import { SaveService } from '../../services/save/save';
import { StateService } from '../../services/state/state';
import { Select } from '../select/select';
import { Toggle } from '../toggle/toggle';
import { FiltersItem } from './item/item';
import { PriceHighOptions } from './price-high-options';
import { PriceLowOptions } from './price-low-options';

@Component({
  imports: [FiltersItem, Select, Toggle],
  selector: 'app-filters',
  styleUrl: './filters.css',
  templateUrl: './filters.html',
})
export class Filters {
  protected readonly filteringService = inject(FilteringService);
  protected readonly listingsService = inject(ListingsService);
  protected readonly saveService = inject(SaveService);
  protected readonly stateService = inject(StateService);

  protected readonly areasOptions = computed(() => {
    const _options = [];
    let family;

    for (const _area of this.filteringService.availableAreas.value() ?? []) {
      if (!_area.title || !_area.id) {
        continue;
      }
      if (_area.title === 'Family Neighborhoods') {
        family = { label: _area.title, value: _area.id };
        continue;
      }

      _options.push({ label: _area.title, value: _area.id });
    }

    if (family) {
      _options.push(family);
    }

    return _options;
  });
  protected readonly bathsOptions = signal([
    { label: '1+', value: '1' },
    { label: '2+', value: '2' },
    { label: '3', value: '3' },
  ]);
  protected readonly bedsOptions = signal([
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4+', value: '4' },
  ]);
  protected readonly checkingForToken = signal(true);
  protected readonly garageOptions = signal([
    { label: '1 Car Garage', value: '1-car' },
    { label: '1 Car, plus Golf Car Garage', value: '1-car-golf' },
    { label: '2 Car Garage', value: '2-car' },
    { label: '2 Car, plus Golf Car Garage', value: '2-car-golf' },
    { label: '3+ Car Garage', value: '3-car' },
    { label: '3+ Car, plus Golf Car Garage', value: '3-car-golf' },
    { label: 'Oversized Garage', value: 'oversized' },
  ]);
  protected readonly homesiteOptions = signal([
    { label: 'Home Golf Front Site', value: 'Home Golf Front Site' },
    { label: 'Corner Site', value: 'Corner Site' },
    { label: 'Rear Privacy with Landscape', value: 'Rear Privacy with Landscape' },
    { label: 'Golf View Site', value: 'Golf View Site' },
    { label: 'Water Front Site', value: 'Water Front Site' },
    { label: 'View Site', value: 'View Site' },
    { label: 'Cul-de-sac', value: 'Cul de Sac Site' },
    { label: 'Water View Site', value: 'Water View Site' },
    { label: 'Preserve Site', value: 'Preserve Site' },
    { label: 'Interior Backs to Street', value: 'Interior Backs to Street' },
    { label: 'Interior Site', value: 'Interior Site' },
    { label: 'Backs to Wall', value: 'Backs to Wall' },
    { label: 'Oversized Homesite, Room for Pool', value: 'Oversized Homesite^ room for pool' },
  ]);
  protected readonly minSqftOptions = signal([
    { label: 'No min', value: '' },
    { label: '1,000', value: '1000' },
    { label: '1,500', value: '1500' },
    { label: '2,000', value: '2000' },
    { label: '2,500', value: '2500' },
    { label: '3,000', value: '3000' },
  ]);
  protected readonly priceHigh = computed(() => PriceHighOptions.call(this));
  protected readonly priceLow = computed(() => PriceLowOptions.call(this));
  protected readonly seriesOptions = signal([
    { label: 'Patio Villas', value: 'patio-villas' },
    { label: 'Cottage Villas', value: 'cottage-villas' },
    { label: 'Cottage/Ranch Homes', value: 'cottage-homes' },
    { label: 'Courtyard Villas', value: 'courtyard-villas' },
    { label: 'Garden Villas', value: 'garden-villas' },
    { label: 'The Verandas', value: 'the-verandas' },
    { label: 'Designer Villas', value: 'designer-villas' },
    { label: 'Designer Homes', value: 'designer-homes' },
    { label: 'Premier Homes', value: 'premier-homes' },
    { label: 'Manufactured Homes', value: 'manufactured' },
  ]);
  // protected readonly seriesOptions = computed(() => {
  //   const options = [];
  //   // Filter out Middleton series in The Villages since those homes aren't available to thi community.
  //   let _series = this.filteringService.availableSeries.value() ?? [];
  //   if (environment.community === 'villages') { _series = _series.filter(s => s.communities && s.communities.findIndex(c => c.id === 'middleton') === -1); }
  //   for (const series of _series) {
  //     if (!series.title || !series.id) { continue; }
  //     options.push({ label: series.title, value: series.id });
  //   }
  //   return options;
  // });
  protected readonly sortByOptions = [
    { label: 'Open Houses', value: 'open' },
    { label: 'Newest', value: 'newest' },
    { label: 'Price: Low to High', value: 'price-low' },
    { label: 'Price: High to Low', value: 'price-high' },
  ];

  constructor() {
    effect(() => {
      this.stateService.userToken();

      untracked(() => {
        if (this.stateService.userToken()) {
          this.checkingForToken.set(false);
        } else {
          this.checkingForToken.set(true);
        }
      });
    });
  }

  protected dreamHomeAddress(listing: Listing): string {
    if (typeof listing.address === 'string') {
      return listing.address;
    }

    const _streetAddress = listing.address.streetAddress;
    if (typeof _streetAddress === 'string' && _streetAddress) {
      return _streetAddress;
    }

    return 'Address unavailable';
  }
}
